/**
 * Carousel navigation hook
 * Extracted from original lines 345-364
 */

import { useState, useCallback } from "react";

/**
 * Hook for carousel/pagination navigation logic
 * Original logic from lines 345-364
 *
 * @param items - Array of items to paginate
 * @param itemsPerPage - Number of items to show at once
 * @returns Carousel state and navigation functions
 */
export const useCarousel = <T>(items: T[], itemsPerPage: number = 3) => {
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);

  const canNavigateBackward = visibleStartIndex > 0;
  const canNavigateForward = visibleStartIndex < items.length - itemsPerPage;

  /**
   * Navigate to next set of items
   * Original logic from handleNext (lines 350-353)
   */
  const handleNext = useCallback(() => {
    if (canNavigateForward) {
      setVisibleStartIndex(visibleStartIndex + 1);
    }
  }, [canNavigateForward, visibleStartIndex]);

  /**
   * Navigate to previous set of items
   * Original logic from handlePrev (lines 356-359)
   */
  const handlePrev = useCallback(() => {
    if (canNavigateBackward) {
      setVisibleStartIndex(visibleStartIndex - 1);
    }
  }, [canNavigateBackward, visibleStartIndex]);

  /**
   * Get visible items for current page
   * Original logic from line 361-364
   */
  const visibleItems = items.slice(visibleStartIndex, visibleStartIndex + itemsPerPage);

  /**
   * Reset carousel to first page
   */
  const reset = useCallback(() => setVisibleStartIndex(0), []);

  return {
    visibleItems,
    canNavigateBackward,
    canNavigateForward,
    handleNext,
    handlePrev,
    currentIndex: visibleStartIndex,
    reset,
  };
};
